'use client'

import { useEffect, useRef, useState } from 'react'

/**
 * La cascade animée, en fond de l'écran de connexion.
 *
 * ------------------------------------------------------------------------
 * ⚠️ La règle : le formulaire ne doit jamais attendre la vidéo
 * ------------------------------------------------------------------------
 * C'est le premier écran que voient 260 apprenants, dont beaucoup depuis un
 * téléphone. Une vidéo de fond est un ornement ; le champ de mot de passe est le
 * service. La vidéo n'est donc **pas dans le HTML servi** : elle n'est demandée
 * qu'après hydratation, et seulement si les conditions ci-dessous sont réunies.
 * Tant qu'elle n'est pas là — ou si elle ne vient jamais — c'est la photo fixe
 * qui tient l'écran, et rien ne manque.
 *
 * Les quatre conditions, dans l'ordre où elles se posent :
 *
 *   1. **`prefers-reduced-motion`.** Un mouvement de fond permanent est
 *      exactement ce que ce réglage demande d'éviter.
 *   2. **Moins de 1020 px de large.** C'est la bascule du gabarit — en dessous,
 *      la carte se recentre et couvre l'essentiel de la photo : on paierait des
 *      données mobiles pour un décor qu'on ne voit plus.
 *   3. **`saveData`.** Le navigateur dit explicitement d'économiser.
 *   4. **Connexion lente** (`2g`, `3g`). 850 ko en 3G, c'est plusieurs secondes
 *      de radio pour un fond.
 *
 * ------------------------------------------------------------------------
 * Ce qui a été mesuré avant de la mettre là
 * ------------------------------------------------------------------------
 * · **Le poids.** Le fichier d'origine fait 17,3 Mo — 5 s de 3524×2352 à
 *   28,7 Mb/s, un débit de montage. Ré-encodé en 1280 de large, H.264 CRF 28,
 *   sans piste audio : **851 ko**, soit 19 fois moins, pour un fond qui passe
 *   sous un voile et que personne ne regarde de près.
 *
 * · **La boucle.** L'original ne bouclait pas : l'écart entre la première et la
 *   dernière image (9,6/255) était plus grand que le mouvement naturel de la
 *   vidéo (4,4/255) — on aurait vu un à-coup toutes les 5 secondes. La fin est
 *   donc fondue sur le début sur 0,6 s ; la couture retombe à 2,5/255, sous le
 *   mouvement propre de la scène. Une lecture en aller-retour aurait suffi aussi,
 *   mais une cascade qui remonte se voit tout de suite.
 *
 * · **Le contraste.** Le voile du gabarit est calibré pour la photo d'oasis. Sur
 *   les 12 images du cycle, la fenêtre la plus claire donne 6,42:1 sous
 *   l'accroche et 10,06:1 sous la carte, contre 6,58 et 10,14 pour la photo
 *   mesurée de la même façon. **2 % d'écart : le voile se transpose tel quel**,
 *   et c'est pour cela que rien n'a été retouché dans `layout.tsx`.
 *
 * ⚠️ **Le pire cas d'une vidéo se cherche sur toute la boucle, pas sur une
 * image.** La mesure ci-dessus balaie douze instants et retient le plus clair.
 *
 * ⚠️ **La photo de repli n'est pas l'attribut `poster`.** `poster` ferait
 * chercher le fichier brut — 763 ko. La photo est rendue par `next/image` dans
 * `layout.tsx`, qui la sert en WebP dimensionné, autour de 170 ko.
 */
export function FondAnime({ cadrage }: { cadrage: string }) {
  const [source, setSource] = useState<string | null>(null)
  const [visible, setVisible] = useState(false)
  const video = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    /* Les refus définitifs d'abord : rien de ce qui suit ne peut changer en
       cours de vie de l'écran, donc on sort une fois pour toutes.

       `connection` n'existe pas sur tous les navigateurs — Safari notamment.
       Son absence n'est pas un refus : on continue. */
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const lien = (
      navigator as Navigator & {
        connection?: { saveData?: boolean; effectiveType?: string }
      }
    ).connection
    if (lien?.saveData) return
    if (lien?.effectiveType && ['slow-2g', '2g', '3g'].includes(lien.effectiveType)) return

    /* ------------------------------------------------------------------
     * ⚠️⚠️ La largeur, elle, se REDEMANDE — et c'est un défaut corrigé
     * ------------------------------------------------------------------
     * Constaté en production le 2026-08-26 : dans un onglet ouvert en
     * arrière-plan, la vidéo ne se montait jamais, et pas davantage en revenant
     * dessus. La cause n'est pas celle qu'on croit — mesurée, pas déduite :
     * **un onglet d'arrière-plan rapporte 957 px de large**, sous le seuil de
     * 1020. La garde refusait sur la LARGEUR, à un moment où cette largeur
     * n'était pas encore la vraie.
     *
     * (Le premier correctif tenté visait `requestIdleCallback`, soupçonné de ne
     * jamais s'exécuter dans un onglet caché. Sondé sur place : il s'exécute en
     * **1 ms**, et `setTimeout` en 301. L'API n'y était pour rien. Le délai de
     * garde ci-dessous reste, il ne coûte rien, mais il ne corrige pas ça.)
     *
     * Le vrai défaut est qu'on ne posait la question qu'UNE FOIS. Un écran qui
     * franchit le seuil plus tard — un onglet qui prend enfin sa taille, une
     * fenêtre qu'on élargit — ne redemandait jamais. On écoute donc la requête
     * média, ce qui couvre les deux cas d'un même geste.
     */
    const assezLarge = window.matchMedia('(min-width: 1020px)')

    /* ⚠️ **On attend l'inactivité du navigateur, et ce n'est pas une politesse
       envers le linter.** Décider dans le corps de l'effet ferait partir la
       requête vidéo dans la même bousculade que la police, la photo et le
       script du formulaire — 850 ko qui se disputeraient la bande passante avec
       ce dont l'apprenant a besoin pour se connecter. En passant par
       `requestIdleCallback`, l'ornement ne part qu'une fois l'écran posé et le
       champ utilisable. `setTimeout` prend le relais là où l'API manque
       (Safari), avec le même effet : après la première peinture. */
    const differer: (f: () => void) => number = window.requestIdleCallback
      ? (f) => window.requestIdleCallback(f, { timeout: 2500 })
      : (f) => window.setTimeout(f, 300)
    const annuler: (h: number) => void = window.cancelIdleCallback
      ? (h) => window.cancelIdleCallback(h)
      : (h) => window.clearTimeout(h)

    let jeton = 0
    function tenter() {
      if (!assezLarge.matches) return
      annuler(jeton)
      jeton = differer(() => setSource('/brand/motion/oasis-cascade.mp4'))
    }

    tenter()
    assezLarge.addEventListener('change', tenter)
    return () => {
      annuler(jeton)
      assezLarge.removeEventListener('change', tenter)
    }
  }, [])

  if (!source) return null

  return (
    <video
      ref={video}
      src={source}
      autoPlay
      muted
      loop
      playsInline
      preload="auto"
      aria-hidden="true"
      tabIndex={-1}
      /* La vidéo se pose PAR-DESSUS la photo, qui reste dessous : si la lecture
         échoue, il n'y a pas de trou, seulement un fond qui ne bouge pas. Elle
         n'apparaît qu'une fois prête, sinon on verrait une image noire le temps
         du premier tampon. */
      onCanPlay={() => setVisible(true)}
      className={`absolute inset-0 size-full object-cover transition-opacity duration-700 ${
        visible ? 'opacity-100' : 'opacity-0'
      }`}
      style={{ objectPosition: cadrage }}
    />
  )
}
